<script setup lang="ts">
/* =============================================================
   VESSEL — CREATE / EDIT
   =============================================================
   The admin panel's form, adapted for a customer: no company
   picker, because a customer only ever registers ships for the
   company they sign in for — company_id comes from the session,
   and RLS would refuse any other.

   One page, two jobs, told apart by whether the route carries an
   id. A full page rather than a dialog, like every record form.
   ============================================================= */

import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import PageHeader from '@/components/ui/PageHeader.vue'
import Panel from '@/components/ui/Panel.vue'
import Button from '@/components/ui/Button.vue'
import TextField from '@/components/ui/TextField.vue'
import SelectField from '@/components/ui/SelectField.vue'
import CountrySelect from '@/components/ui/CountrySelect.vue'
import AmenityPicker from '@/components/ui/AmenityPicker.vue'
import Alert from '@/components/ui/Alert.vue'
import { routes, to } from '@/config/routes'
import { useAuth } from '@/lib/auth/useAuth'
import { useDocumentTitle } from '@/lib/composables/useDocumentTitle'
import { createVessel, getVessel, updateVessel } from '@/lib/vessels'
import { useVesselTypes } from '@/lib/vesselTypes'
import { useUnsavedGuard } from '@/lib/useUnsavedGuard'
import { errorMessage } from '@/lib/errors'
import type { AmenityCode, VesselInput, VesselType } from '@/lib/types'

const route = useRoute()
const router = useRouter()
const { user } = useAuth()
const { vesselTypes } = useVesselTypes()

const editingId = computed(() => (route.params.id ? Number(route.params.id) : null))
const isEdit = computed(() => editingId.value !== null)

interface FormState {
  name: string
  /** '' until a type is picked, which is not a VesselType. */
  type: string
  length_m: string
  width_m: string
  draft_m: string
  /** '' = no flag given. */
  flag_code: string
  service_needs: AmenityCode[]
}

const blank: FormState = {
  name: '',
  type: '',
  length_m: '',
  width_m: '',
  draft_m: '',
  flag_code: '',
  service_needs: [],
}

const form = ref<FormState>({ ...blank })
/** What was loaded, to tell a real edit from a visit. */
const pristine = ref<FormState>({ ...blank })
const heading = ref('New vessel')

useDocumentTitle(() => (isEdit.value ? `Edit ${heading.value}` : 'New vessel'))

const loading = ref(false)
const saving = ref(false)
const notFound = ref(false)
const formError = ref('')
const errors = ref<Record<string, string>>({})

const dirty = computed(() => JSON.stringify(form.value) !== JSON.stringify(pristine.value))

onMounted(async () => {
  if (!isEdit.value) return
  loading.value = true
  try {
    const vessel = await getVessel(editingId.value as number)
    if (!vessel) {
      notFound.value = true
      return
    }
    const state: FormState = {
      name: vessel.name,
      type: vessel.type,
      length_m: String(vessel.length_m),
      width_m: String(vessel.width_m),
      draft_m: String(vessel.draft_m),
      flag_code: vessel.flag_code ?? '',
      service_needs: [...vessel.service_needs],
    }
    form.value = { ...state, service_needs: [...state.service_needs] }
    pristine.value = state
    heading.value = vessel.name
  } catch (e) {
    formError.value = errorMessage(e, 'Could not load the vessel.')
  } finally {
    loading.value = false
  }
})

/**
 * One dimension, in metres: required (NOT NULL), positive (the CHECK), and
 * under the column's precision so Postgres never answers with an overflow.
 */
function dimension(raw: string, max: number): { value: number | null; error?: string } {
  const text = raw.trim().replace(',', '.')
  if (text === '') return { value: null, error: 'Required, in metres.' }
  const value = Number(text)
  if (Number.isNaN(value)) return { value: null, error: 'Enter a number, in metres.' }
  if (value <= 0) return { value: null, error: 'Must be greater than zero.' }
  if (value >= max) return { value: null, error: `Must be less than ${max} m.` }
  return { value }
}

function validate(): VesselInput | null {
  const next: Record<string, string> = {}

  const name = form.value.name.trim()
  if (!name) next.name = 'Enter a name.'

  const type = form.value.type
  if (!type) next.type = 'Choose a type.'

  // Ceilings are numeric(6,2), numeric(5,2) and numeric(4,2) respectively.
  const length = dimension(form.value.length_m, 10000)
  if (length.error) next.length_m = length.error
  const width = dimension(form.value.width_m, 1000)
  if (width.error) next.width_m = width.error
  const draft = dimension(form.value.draft_m, 100)
  if (draft.error) next.draft_m = draft.error

  const companyId = user.value?.companyId
  if (!companyId) formError.value = 'Your account is not linked to a company. Contact River Atlas.'

  errors.value = next
  if (Object.keys(next).length > 0 || !companyId) return null

  return {
    company_id: companyId,
    name,
    type: type as VesselType,
    length_m: length.value as number,
    width_m: width.value as number,
    draft_m: draft.value as number,
    flag_code: form.value.flag_code || null,
    service_needs: form.value.service_needs,
  }
}

async function onSubmit(): Promise<void> {
  if (saving.value) return
  formError.value = ''
  const input = validate()
  if (!input) return

  saving.value = true
  try {
    const saved = isEdit.value
      ? await updateVessel(editingId.value as number, input)
      : await createVessel(input)
    // Clear the guard before navigating, or leaving asks about saved changes.
    pristine.value = { ...form.value }
    await router.push(to.vessel(saved.id))
  } catch (e) {
    formError.value = errorMessage(e, 'Could not save the vessel.')
  } finally {
    saving.value = false
  }
}

const cancelTo = computed(() =>
  isEdit.value ? to.vessel(editingId.value as number) : routes.vessels,
)

useUnsavedGuard(dirty, saving)
</script>

<template>
  <PageHeader
    :title="isEdit ? `Edit ${heading}` : 'New vessel'"
    description="This information allows the platform to filter out locations that are clearly unsuitable."
  />

  <p v-if="loading" class="page-status">Loading…</p>

  <Alert v-else-if="notFound" tone="danger">
    That vessel does not exist. <RouterLink :to="routes.vessels">Back to your vessels</RouterLink>.
  </Alert>

  <Panel v-else>
    <form class="form-layout" novalidate @submit.prevent="onSubmit">
      <Alert v-if="formError" tone="danger">{{ formError }}</Alert>

      <div class="form-grid">
        <TextField
          v-model="form.name"
          label="Name"
          :error="errors.name"
          placeholder="MS Rheinfels"
          autocomplete="off"
          class="form-wide"
          required
        />
        <SelectField v-model="form.type" label="Type" placeholder="Select a type" :error="errors.type">
          <option v-for="t in vesselTypes" :key="t.code" :value="t.code">{{ t.label }}</option>
        </SelectField>
        <TextField
          v-model="form.length_m"
          label="Length"
          :error="errors.length_m"
          inputmode="decimal"
          hint="Metres, length overall."
          placeholder="110"
          autocomplete="off"
          required
        />
        <TextField
          v-model="form.width_m"
          label="Beam"
          :error="errors.width_m"
          inputmode="decimal"
          hint="Metres, at the widest point."
          placeholder="11.4"
          autocomplete="off"
          required
        />
        <TextField
          v-model="form.draft_m"
          label="Draft"
          :error="errors.draft_m"
          inputmode="decimal"
          hint="Metres, fully laden."
          placeholder="1.6"
          autocomplete="off"
          required
        />
        <CountrySelect v-model="form.flag_code" label="Flag" hint="Optional." optional />
        <AmenityPicker
          v-model="form.service_needs"
          label="Service needs"
          hint="What the ship usually needs at a berth. Pre-selected when you search."
          class="form-wide"
        />
      </div>

      <footer class="form-actions">
        <Button variant="ghost" :to="cancelTo" :disabled="saving">Cancel</Button>
        <Button type="submit" :loading="saving">
          {{ isEdit ? 'Save changes' : 'Add vessel' }}
        </Button>
      </footer>
    </form>
  </Panel>
</template>
